import React from 'react';
import { Dropdown, Menu, Popconfirm, Tag, Tooltip } from 'antd';
import { DeleteOutlined, MoreOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getUserDetails, STUDY_DATETIME_FORMAT } from '../../../../utils/helper';
import type { OoReport, PacsOrder, Report } from '../../types';
import { getStatusStyle } from './statusStyles';

interface ReportHistoryProps {
  reports: Report[];
  /** doc_id currently open in the editor — used to highlight the loaded row */
  activeDocId?: number | null;
  /** pr_id of the report the editor is acting ON (the commit target). Needed because the
   *  editing SURFACE is not always one of these rows: re-reporting a signed study — and
   *  applying a template to one — edits a throwaway scratch document that has no report
   *  row, so activeDocId matches nothing here. */
  activeReportId?: number | null;
  /** true once the order has a signed report — historical drafts open read-only then */
  orderSignedOff?: boolean;
  /** pr_id of the NEWEST sign-off. An order can hold several (one per re-report); the newest
   *  is the report of record. Every sign-off opens for re-reporting, so this only changes the
   *  wording — an older one starts the amendment from a superseded version. */
  latestSignedReportId?: number | null;
  /** the order these reports belong to — every row is the same study, so its
   *  modality/body part is rendered once per row purely as an anchor */
  pacsOrder?: PacsOrder | null;
  onLoad: (report: Report) => void;
  onDelete: (report: Report) => void;
}

const isAutosaved = (r: Report) => !r.pr_status || r.pr_status === 'AUTOSAVED';

const ReportHistory: React.FC<ReportHistoryProps> = ({
  reports, activeDocId, activeReportId, orderSignedOff = false, latestSignedReportId = null,
  pacsOrder, onLoad, onDelete,
}) => {
  const currentUser = getUserDetails()?.username;
  const studyLabel =
    [pacsOrder?.po_modality, pacsOrder?.po_body_part].filter(Boolean).join(' · ') || '—';

  // A private autosave entry only appears once OnlyOffice has actually written content
  // (doc_version > 1) — mirrors the old app where the entry shows up on first autosave.
  const visibleReports = reports.filter(record => {
    const rec = record as OoReport;
    return !(isAutosaved(record) && (rec.document?.doc_version ?? 1) <= 1);
  });

  if (!visibleReports.length) {
    return <div className="no-content-message">No reports available</div>;
  }

  // Is the open document one of the rows below? On a signed order it usually is NOT: the
  // editor opens a scratch fork of the signed snapshot, so activeDocId is an id that appears
  // nowhere in this list and every row would render unselected.
  //
  // Matching on the document is still the rule whenever it CAN match, because that is the
  // only thing that distinguishes rows when the action context alone does not: browsing a
  // historical draft on a signed order moves activeDocId but deliberately leaves the signed
  // report as the action context, and highlighting on the report there would mark the signed
  // row while the user is reading a different one.
  const rowDocIds = new Set(
    visibleReports.map(r => (r as OoReport).document?.doc_id).filter(Boolean)
  );
  const surfaceIsOffList = !activeDocId || !rowDocIds.has(activeDocId);

  return (
    <div className="report-history-list">
      {visibleReports.map(record => {
        const rec = record as OoReport;
        const docId = rec.document?.doc_id;
        const isActive = surfaceIsOffList
          ? (activeReportId != null && record.pr_id === activeReportId)
          : (!!docId && docId === activeDocId);
        const isSigned = record.pr_status === 'SIGNEDOFF';
        const creator = rec.pr_created_by || rec.pr_report_drafted_by || rec.pr_reported_by;
        // Signed entries are immutable records; draft/reviewed/autosaved entries can only
        // be deleted by the user who created them (backend enforces the same).
        const isDeleteLocked = isSigned || (!!creator && creator !== currentUser);
        // Once the order is signed off, historical drafts/reviews open read-only. Every
        // SIGNED entry stays openable for re-reporting — including a superseded one, which
        // starts the amendment from that older version (nothing persists unless it is signed
        // off again, and the sign-off appends a new version rather than replacing any).
        const isCurrentSigned =
          isSigned && (latestSignedReportId == null || record.pr_id === latestSignedReportId);
        const opensReadOnly = !isSigned && orderSignedOff;

        const menu = (
          <Menu>
            <Menu.Item key="load" icon={<EyeOutlined />} onClick={() => onLoad(record)}>
              {opensReadOnly ? 'View' : 'Open'}
            </Menu.Item>
            <Menu.Item key="delete" icon={<DeleteOutlined />} disabled={isDeleteLocked}>
              <Popconfirm
                title="Delete report?"
                description="This will permanently delete the report and its document. This action cannot be undone."
                onConfirm={() => onDelete(record)}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={isDeleteLocked}
              >
                Delete
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <div
            key={record.pr_id}
            className={`report-history-row${isActive ? ' active' : ''}`}
            onClick={() => onLoad(record)}
            title={
              isCurrentSigned ? 'Open the signed report (edits are only kept if you sign off again)'
                : isSigned ? 'Re-report from this earlier version (replaces your current working copy)'
                : opensReadOnly ? 'View this historical version (read-only)'
                : 'Open this report'
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              marginBottom: 4,
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              borderLeft: `3px solid ${isActive ? '#1677ff' : 'transparent'}`,
              background: isActive ? '#E6F4FF' : 'transparent',
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Line 1: status + study */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <Tag
                  style={{ ...getStatusStyle(record.pr_status || 'AUTOSAVED'), margin: 0, fontSize: 11, lineHeight: '18px', flex: 'none' }}
                >
                  {isAutosaved(record) ? 'AUTO-SAVED' : record.pr_status?.replaceAll('_', ' ')}
                </Tag>
                <span
                  style={{
                    color: '#0F172A',
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {studyLabel}
                </span>
                {isActive && (
                  <span
                    title="Loaded in the editor"
                    style={{ color: '#1677ff', fontSize: 10, lineHeight: 1, flex: 'none' }}
                  >
                    ●
                  </span>
                )}
              </div>
              {/* Line 2: datetime + user */}
              <div
                style={{
                  color: '#334155',
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {moment(record.createdAt).format(STUDY_DATETIME_FORMAT)} · {record.pr_reported_by || '—'}
              </div>
            </div>
            <Tooltip title="Actions">
              <Dropdown overlay={menu} trigger={['click']}>
                <MoreOutlined
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: '1rem', cursor: 'pointer' }}
                />
              </Dropdown>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

export default ReportHistory;
